import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { baseUrl } from "../../../Environments/environment";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { apiClient } from "../../../api";
import { QR_CODE_KIT_SHOP_ITEMS } from "../QRCodeKit/qrCodeKitShopCatalog";

const WORKFLOW_REVIEW_PATH = "/dashboard/configure/workflow";
const QR_CODE_KIT_SHOP_PATH = "/dashboard/qr-code-kit";
const TEAM_MEMBER_PATH = "/dashboard/team-member";
const WALLET_PATH = "/dashboard/wallet";

/** Matches minimum purchase in `AddCredits.jsx` / wallet validation (hint copy only). */
const MIN_CREDIT_TOPUP_BALANCE = 500;

function hasText(v) {
	return v !== undefined && v !== null && String(v).trim() !== "";
}

function normalizeNetwork(network) {
	return String(network ?? "")
		.trim()
		.toLowerCase();
}

function jobReviewPageUrl(job) {
	return (
		job.networkUrl ??
		job.networkURL ??
		job.NetworkUrl ??
		job.reviewPageUrl ??
		""
	);
}

function jobRedirectUrlField(job) {
	return job.redirectUrl ?? job.redirectURL ?? job.RedirectUrl ?? "";
}

function buildPlatformIntegrationItem(workflow, reviewJobs, reviewJobsLoaded) {
	const reviewEnabled = Boolean(workflow?.reviewFlow?.reviewsEnabled);
	const jobs = Array.isArray(reviewJobs) ? reviewJobs : [];
	const hasUsablePlatform = jobs.some(
		(job) =>
			hasText(normalizeNetwork(job.network)) &&
			hasText(jobReviewPageUrl(job)) &&
			hasText(jobRedirectUrlField(job)),
	);
	const completed = reviewEnabled && reviewJobsLoaded && hasUsablePlatform;
	return {
		id: "platform-integration",
		label: "Platforms integration",
		hint: completed
			? "Guest review redirection is active through connected review platforms."
			: "Connects external review platforms so guests can be redirected to leave public reviews.",
		to: WORKFLOW_REVIEW_PATH,
		completed,
	};
}

/** Same rules as QRCodeKit.jsx `fetchOrderDetails` (active orders only). */
function extractPurchasedQrKitItemIds(orders) {
	const orderedIds = new Set();
	if (!Array.isArray(orders)) return orderedIds;
	for (const order of orders) {
		const st = order.orderStatus;
		if (
			(st === "placed" ||
				st === "dispatched" ||
				st === "processing") &&
			Array.isArray(order.orderItems)
		) {
			for (const orderItem of order.orderItems) {
				if (orderItem.itemId != null) {
					orderedIds.add(Number(orderItem.itemId));
				}
			}
		}
	}
	return orderedIds;
}

function buildQrKitPurchaseItem(purchasedIdList, qrKitOrdersLoaded) {
	const purchased = new Set(
		(purchasedIdList ?? []).map((id) => Number(id)),
	);
	const completed = qrKitOrdersLoaded && purchased.size > 0;
	return {
		id: "qr-kit-purchase",
		label: "QR Kit purchase",
		hint: completed
			? "Physical QR touchpoints are live for this property. Guests can scan and jump straight into your review flow."
			: "QR touchpoints are not active yet. These items let guests scan on-site and open your review flow in one step.",
		to: QR_CODE_KIT_SHOP_PATH,
		completed,
	};
}

/** Same endpoint as `TeamMemberManagement` (`GET /user/:propertyId`). */
function buildTeamMemberItem(teamUsers, teamUsersLoaded) {
	const list = Array.isArray(teamUsers) ? teamUsers : [];
	const hasInvitedMember = list.some((m) => Boolean(m?.isInvited));
	const completed =
		teamUsersLoaded && (list.length > 1 || hasInvitedMember);
	return {
		id: "team-member-management",
		label: "Team Member Management",
		hint: completed
			? "This listing has multiple team members and/or pending invitations. You can adjust roles or invite more people anytime."
			: "Invite colleagues so they can help manage reviews, workflows, and guest outreach for this property.",
		to: TEAM_MEMBER_PATH,
		completed,
	};
}

/**
 * Mirrors `Wallet.jsx` `loadRecentTrans()`:
 *   const data = response.data.result; // already an array
 * Task is "completed" when any row's `type === "manual_topup"`.
 */
function hasManualTopUp(transactions) {
	const list = Array.isArray(transactions) ? transactions : [];
	return list.some((t) => t?.type === "manual_topup");
}

function buildCreditTopUpItem(transactions, transactionsLoaded) {
	const completed = transactionsLoaded && hasManualTopUp(transactions);
	return {
		id: "credit-top-up",
		label: "Credit top-up",
		hint: completed
			? "At least one manual credit top-up is on record for this listing. You can add more or manage auto top-up from the wallet anytime."
			: `Add credits manually (wallet → Add credits) so SMS, workflows, and paid features keep running. Minimum purchase is ${MIN_CREDIT_TOPUP_BALANCE} credits.`,
		to: WALLET_PATH,
		completed,
	};
}

async function fetchDashboardSetupPanel(propertyId) {
	const workflowRes = await apiClient
		.get(`${baseUrl}/workflow/${propertyId}`)
		.catch(() => ({ status: 0 }));
	const workflow =
		workflowRes.status === 200 ? workflowRes.data?.result ?? null : null;

	let reviewJobs = [];
	let reviewJobsLoaded = false;
	try {
		const jobsRes = await apiClient.get(
			`${baseUrl}/reviews/jobs/${propertyId}`,
		);
		const list = jobsRes.data?.result?.reviewJobs;
		const sc = jobsRes.data?.statusCode;
		const jobsResponseOk =
			sc === 200 ||
			sc === "200" ||
			(jobsRes.status === 200 && Array.isArray(list));
		if (jobsResponseOk) {
			reviewJobs = Array.isArray(list) ? list : [];
			reviewJobsLoaded = true;
		}
	} catch {
		// Non-blocking: workflow-based items still apply; jobs section deferred
	}

	let qrKitPurchasedItemIds = [];
	let qrKitOrdersLoaded = false;
	try {
		const qrRes = await apiClient.get(`${baseUrl}/qrKitOrder/${propertyId}`);
		if (qrRes.status === 200) {
			const orders = qrRes.data?.result?.qrKitOrders || [];
			qrKitPurchasedItemIds = Array.from(
				extractPurchasedQrKitItemIds(orders),
			);
			qrKitOrdersLoaded = true;
		}
	} catch {
		// Omit QR rows if orders cannot be loaded
	}

	let teamUsers = [];
	let teamUsersLoaded = false;
	try {
		const teamRes = await apiClient.get(`/user/${propertyId}`);
		if (teamRes.status === 200) {
			const raw = teamRes.data?.result;
			teamUsers = Array.isArray(raw) ? raw : [];
			teamUsersLoaded = true;
		}
	} catch {
		// Non-blocking: panel still shows other items
	}

	// Same fetch as `Wallet.jsx` `loadRecentTrans()` — `response.data.result` is already an array.
	let walletTransactions = [];
	let walletTransactionsLoaded = false;
	try {
		const txRes = await apiClient.get(
			`${baseUrl}/stripe/transactions/${propertyId}?recent=${true}`,
		);
		const data = txRes?.data?.result;
		walletTransactions = Array.isArray(data) ? data : [];
		walletTransactionsLoaded = true;
	} catch {
		// Credit row stays pending if transactions cannot be loaded
	}

	return {
		workflow,
		reviewJobs,
		reviewJobsLoaded,
		qrKitPurchasedItemIds,
		qrKitOrdersLoaded,
		teamUsers,
		teamUsersLoaded,
		walletTransactions,
		walletTransactionsLoaded,
	};
}

export default function ProfileSetupPendingPanel() {
	const { selectedPropertyId } = useProperty();
	const [drawerOpen, setDrawerOpen] = useState(false);

	const { data, isPending } = useQuery({
		queryKey: ["dashboardSetupPanel", selectedPropertyId],
		queryFn: () => fetchDashboardSetupPanel(selectedPropertyId),
		enabled: Boolean(selectedPropertyId),
		staleTime: 60_000,
	});

	// Auto-open logic: only once per session
	useEffect(() => {
		if (!isPending && data && selectedPropertyId) {
			const sessionKey = `onelyk_setup_auto_opened_${selectedPropertyId}`;
			const alreadyOpened = sessionStorage.getItem(sessionKey);
			if (!alreadyOpened) {
				setDrawerOpen(true);
				sessionStorage.setItem(sessionKey, "true");
			}
		}
	}, [isPending, data, selectedPropertyId]);

	const setupSummary = useMemo(() => {
		const platformIntegration = buildPlatformIntegrationItem(
			data?.workflow,
			data?.reviewJobs ?? [],
			Boolean(data?.reviewJobsLoaded),
		);
		const qrKitPurchase = buildQrKitPurchaseItem(
			data?.qrKitPurchasedItemIds,
			Boolean(data?.qrKitOrdersLoaded),
		);
		const teamMemberManagement = buildTeamMemberItem(
			data?.teamUsers,
			Boolean(data?.teamUsersLoaded),
		);
		const creditTopUp = buildCreditTopUpItem(
			data?.walletTransactions,
			Boolean(data?.walletTransactionsLoaded),
		);
		const items = [
			platformIntegration,
			teamMemberManagement,
			creditTopUp,
			qrKitPurchase,
		];
		const totalChecks = items.length;
		const pendingChecks = items.filter((item) => !item.completed).length;
		const completedChecks = Math.max(totalChecks - pendingChecks, 0);
		const completedPercent =
			totalChecks > 0
				? Math.round((completedChecks / totalChecks) * 100)
				: 0;
		const pendingPercent =
			totalChecks > 0 ? Math.max(100 - completedPercent, 0) : 0;
		return {
			items,
			totalChecks,
			pendingChecks,
			completedChecks,
			completedPercent,
			pendingPercent,
		};
	}, [data]);

	useEffect(() => {
		if (!drawerOpen) return;
		const onKey = (e) => {
			if (e.key === "Escape") setDrawerOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [drawerOpen]);

	if (!selectedPropertyId || isPending) {
		return null;
	}

	const count = setupSummary.pendingChecks;

	const panel = (
		<>
			<button
				type="button"
				onClick={() => setDrawerOpen(true)}
				className="fixed right-0 top-[28%] z-[80] flex -translate-y-1/2 flex-col items-center gap-0.5 rounded-l-lg border border-r-0 border-gray-200 bg-white py-2 pl-2 pr-1 shadow-md transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:top-[22%] sm:py-2.5"
				aria-label={`Pending setup, ${count} items`}
				aria-expanded={drawerOpen}
				aria-haspopup="dialog"
			>
				<ClipboardDocumentListIcon
					className="h-5 w-5 shrink-0 text-indigo-600"
					aria-hidden
				/>
				<span className="inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
					{count}
				</span>
			</button>

			{drawerOpen ? (
				<>
					<button
						type="button"
						className="fixed inset-0 z-[90] bg-black/30"
						aria-label="Close pending setup"
						onClick={() => setDrawerOpen(false)}
					/>
					<aside
						className="fixed right-2 top-3 z-[100] flex w-[min(26rem,calc(100vw-1rem))] max-w-[26rem] max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl sm:right-4 sm:top-6 sm:w-[22rem] sm:max-w-[22rem] sm:max-h-[calc(100dvh-3rem)]"
						role="dialog"
						aria-modal="true"
						aria-labelledby="pending-setup-title"
					>
						<div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5 sm:py-4">
							<div className="min-w-0">
								<p
									id="pending-setup-title"
									className="text-sm font-bold text-gray-900 truncate sm:text-base"
								>
									Pending setup
								</p>
								<p className="text-xs text-gray-500 mt-0.5">
									{setupSummary.completedChecks}/{setupSummary.totalChecks} completed
								</p>
							</div>
							<button
								type="button"
								onClick={() => setDrawerOpen(false)}
								className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
								aria-label="Close"
							>
								<XMarkIcon className="h-5 w-5" aria-hidden />
							</button>
						</div>
						<div className="min-h-0 flex-1 overflow-y-auto hide-scrollbar px-4 py-4 sm:px-5 sm:py-5">
							<div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
								<p className="text-xs font-semibold text-gray-900">Setup stats</p>
								<div className="mt-2 flex items-center gap-3">
									<div
										className="relative h-14 w-14 shrink-0 rounded-full"
										style={{
											background: `conic-gradient(#4f46e5 0 ${setupSummary.completedPercent}%, #f59e0b ${setupSummary.completedPercent}% 100%)`,
										}}
									>
										<div className="absolute inset-[5px] grid place-items-center rounded-full bg-white text-[10px] font-semibold text-gray-700">
											{setupSummary.completedPercent}%
										</div>
									</div>
									<div className="min-w-0 flex-1 text-[11px] text-gray-700">
										<p>
											<span className="font-semibold text-gray-900">
												{setupSummary.completedChecks}
											</span>{" "}
											completed
										</p>
										<p>
											<span className="font-semibold text-gray-900">
												{setupSummary.pendingChecks}
											</span>{" "}
											pending
										</p>
									</div>
								</div>
								<div className="mt-3">
									<div className="h-2 w-full overflow-hidden rounded-full bg-indigo-100">
										<div
											className="h-full rounded-full bg-indigo-600 transition-all"
											style={{ width: `${setupSummary.completedPercent}%` }}
										/>
									</div>
									<p className="mt-1 text-[10px] text-gray-600">
										{setupSummary.completedPercent}% complete • {setupSummary.pendingPercent}% pending
									</p>
								</div>
							</div>
							<ul className="space-y-3">
								{setupSummary.items.map((item) => (
									<li key={item.id}>
										<Link
											to={item.to ?? WORKFLOW_REVIEW_PATH}
											onClick={() => setDrawerOpen(false)}
											className={`block rounded-xl border bg-white px-4 py-3 shadow-sm transition sm:px-5 sm:py-4 ${
												item.completed
													? "border-emerald-200 bg-emerald-50/40"
													: "border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow"
											}`}
										>
											<div className="flex items-start justify-between gap-3">
												<p className="text-xs font-medium text-gray-900 leading-snug">
												{item.label}
												</p>
												<span
													className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
														item.completed
															? "bg-emerald-100 text-emerald-700"
															: "bg-amber-100 text-amber-800"
													}`}
												>
													{item.completed ? "Completed" : "Pending"}
												</span>
											</div>
											<p className="mt-0.5 text-[11px] text-gray-600 leading-snug line-clamp-3">
												{item.hint}
											</p>
											<span className="mt-1 inline-block text-[11px] font-medium text-indigo-600">
												{item.completed ? "Completed" : "Complete now"}
											</span>
										</Link>
									</li>
								))}
							</ul>
						</div>
					</aside>
				</>
			) : null}
		</>
	);

	return createPortal(panel, document.body);
}
